import * as React from 'react';

import cx from 'classnames';

import KeyConstants from 'constants/KeyConstants';
import { findDOMNodeOrThrow } from 'utils/findDOMNodeOrThrow';
import focusNode from 'utils/focusNode';
import pickTextareaElementProps from 'utils/pickTextareaElementProps';

import { ChangeHandler, KeyboardEventHandler } from 'types/typescript/eventHandlers';

import './styles/Textarea.scss';

const BORDER_WIDTH = 2;

/**
 * ## Renders a styled [Textarea](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea).
 *
 * ```example
 * <Field>
 *   <Label for="textarea-1" hidden>Label</Label>
 *   <Textarea
 *     autogrow
 *     id="textarea-1"
 *     placeholder="Textarea"
 *   />
 * </Field>
 * ```
 *
 * ```example
 * <Field>
 *   <Label for="textarea-2" hidden>Label</Label>
 *   <Textarea
 *     autoselect
 *     id="textarea-2"
 *     value="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor."
 *   />
 * </Field>
 * ```
 */

type Props = {
  className?: string;
  autofocus?: boolean;
  autogrow?: boolean;
  autoselect?: boolean;
  /** Native */
  placeholder?: string;
  /** Native */
  rows?: number;
  /** Native */
  value: string;
  onHeightChange?: () => void;
  onReturnKeyPress?: (e: React.KeyboardEvent) => boolean;
  onUpdate?: () => void;
  onBlur?: () => void;
  onChange: ChangeHandler<HTMLTextAreaElement>;
  uncontrolled?: boolean;
  defaultValue?: string;
  children?: string;
  disabled?: boolean;
};

class Textarea extends React.Component<Props> {
  height: number | null = null;

  componentDidMount() {
    if (this.props.autogrow) {
      this.recalculateSize();
      window.addEventListener('resize', this.recalculateSize, false);
    }

    if (this.props.autofocus) {
      // Prevents the cursor from being placed at the start of the text
      const { value } = this.getInputNode();
      this.getInputNode().value = '';
      focusNode(this.getInputNode());
      this.getInputNode().value = value;
    }

    if (this.props.autoselect) {
      this.select();
    }
  }

  componentDidUpdate() {
    if (this.props.autogrow) {
      this.recalculateSize();
    }
    if (this.props.onUpdate) {
      this.props.onUpdate();
    }
  }

  componentWillUnmount() {
    if (this.props.autogrow) {
      window.removeEventListener('resize', this.recalculateSize, false);
    }
  }

  getInputNode: () => HTMLTextAreaElement = () => {
    return findDOMNodeOrThrow(this.refs.textarea, HTMLTextAreaElement);
  };

  select = () => {
    this.getInputNode().select();
  };

  focus = () => {
    this.getInputNode().focus();
  };

  clear = () => {
    this.getInputNode().value = '';
  };

  handleChange: ChangeHandler<HTMLTextAreaElement> = event => {
    if (this.props.autogrow) {
      this.recalculateSize();
    }

    // do stuff here

    this.props.onChange(event);
  };

  handleKeyDown: KeyboardEventHandler = event => {
    if (this.props.onReturnKeyPress && event.keyCode === KeyConstants.ENTER) {
      if (this.props.onReturnKeyPress(event)) {
        event.stopPropagation();
      }
    }
  };

  recalculateSize = () => {
    const node = findDOMNodeOrThrow(this, HTMLElement);
    node.style.height = 'auto';

    const height = node.scrollHeight + BORDER_WIDTH;
    node.style.height = `${height}px`;

    if (this.height !== height) {
      this.height = height;
      if (this.props.onHeightChange) {
        this.props.onHeightChange();
      }
    }
  };

  render() {
    const { children, className, placeholder, value, rows, ...others } = this.props;
    let initialValue = '';

    if (value) {
      initialValue = value;
    } else if (children) {
      initialValue = children;
    }

    const props = pickTextareaElementProps(others);

    return (
      <textarea
        {...props}
        rows={rows || 3}
        ref="textarea"
        placeholder={placeholder}
        onKeyDown={this.handleKeyDown}
        className={cx('Textarea', className)}
        value={this.props.uncontrolled ? undefined : initialValue}
        onChange={this.handleChange}>
        {this.props.uncontrolled ? this.props.defaultValue : this.props.children}
      </textarea>
    );
  }
}

export default Textarea;
